import { useState, useRef, useEffect } from 'react';
import { useEditorStore } from '../../stores/editor-store';
import { Sparkles, Loader2, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { useAiActivityStore } from '../../stores/ai-activity-store';
import { useProjectsStore } from '../../stores/projects-store';
import { constructPromptWithSettings } from '../../utils/prompt-utils';
import {
    generateElementWithGlm,
    getCallableErrorMessage,
} from '../../services/ai-functions';
import { useAiAccessStore } from '../../stores/ai-access-store';

export function FloatingEditPanel() {
    const editor = useEditorStore((s) => s.editorInstance);
    const { isOpen, x, y } = useEditorStore((s) => s.floatingPanel);
    const setFloatingPanel = useEditorStore((s) => s.setFloatingPanel);

    const startAiActivity = useAiActivityStore((s) => s.start);
    const stopAiActivity = useAiActivityStore((s) => s.stop);
    const currentProject = useProjectsStore((s) => s.currentProject);
    const setUsage = useAiAccessStore((s) => s.setUsage);

    const [prompt, setPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const inputRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleClose = () => {
        setFloatingPanel({ isOpen: false, x: 0, y: 0 });
    };

    const handleEditElement = async () => {
        if (!editor || !prompt.trim()) return;

        const selected = editor.getSelected();
        if (!selected) {
            setError('No element selected');
            return;
        }

        setIsGenerating(true);
        startAiActivity();
        setError(null);

        try {
            const currentHTML = selected.toHTML();
            const currentHtmlTrimmed =
                currentHTML.length > 2500 ? `${currentHTML.slice(0, 2500)}\n<!-- truncated -->` : currentHTML;

            const promptWithSettings = constructPromptWithSettings(prompt, currentProject?.generationSettings);

            const fullPrompt = `You are editing an existing HTML element. Current element:
${currentHtmlTrimmed}
User's edit request: ${promptWithSettings}
Generate ONLY the modified HTML element with Tailwind CSS classes. Keep the same structure unless specifically asked to change it. Return ONLY the HTML code without explanations or markdown code blocks.`;

            const result = await generateElementWithGlm(fullPrompt);
            const newHTML = result.html;
            setUsage(result.usage);

            const parent = selected.parent();
            const index = parent?.components().indexOf(selected) ?? 0;

            if (parent) {
                // We need to insert the new component and select it
                const [newComponent] = parent.append(newHTML, { at: index });
                selected.remove();

                // Select the new component to keep the flow
                if (newComponent) {
                    editor.select(newComponent);
                }
            }

            setPrompt('');
            handleClose();
        } catch (err) {
            const message = getCallableErrorMessage(err);
            setError(message);
        } finally {
            setIsGenerating(false);
            stopAiActivity();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleEditElement();
        }
        if (e.key === 'Escape') {
            handleClose();
        }
    };

    return (
        <div
            className="fixed z-50 w-80 bg-surface border border-surface-lighter rounded-lg shadow-xl p-3 flex flex-col gap-2"
            style={{ left: x, top: y }}
        >
            <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-text">Edit Element with AI</span>
                <button onClick={handleClose} className="text-text-muted hover:text-text">
                    <X size={14} />
                </button>
            </div>

            {error && (
                <div className="px-2 py-1.5 rounded bg-red-900/30 text-red-300 text-xs">
                    {error}
                </div>
            )}

            <div className="flex gap-2 items-end">
                <textarea
                    ref={inputRef}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Make it bigger, blue..."
                    rows={2}
                    disabled={isGenerating}
                    className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-sm text-gray-900 placeholder:text-gray-400 resize-none focus:outline-none focus:border-accent disabled:opacity-50"
                />
                <Button
                    onClick={handleEditElement}
                    disabled={isGenerating || !prompt.trim()}
                    size="sm"
                >
                    {isGenerating ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : (
                        <Sparkles size={14} />
                    )}
                </Button>
            </div>
        </div>
    );
}
